import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileCode2, Download, Eye, Loader2, Plus, Trash2, Sparkles, AlertTriangle } from 'lucide-react';
import { ics2API } from '@/lib/api';
import { toast } from 'sonner';

const TRANSPORT_MODES = [
  { value: '1', label: '1 — Sea' },
  { value: '2', label: '2 — Rail' },
  { value: '3', label: '3 — Road' },
  { value: '4', label: '4 — Air' },
  { value: '8', label: '8 — Inland waterway' },
];

const emptyParty = () => ({ eori: '', name: '', street: '', city: '', postcode: '', country: '' });
const emptyItem = (n = 1) => ({
  item_number: n, description: '', hs_code: '', gross_mass_kg: 0, net_mass_kg: null,
  quantity: null, quantity_unit: 'KGM', country_of_origin: '', cbam_embedded_co2_t: null,
});

export default function ICS2Generator() {
  const [form, setForm] = useState({
    lrn: '', declaration_type: 'ENS', transport_mode: '1',
    conveyance_id: '', arrival_port: '', loading_port: '',
    estimated_arrival: new Date().toISOString().slice(0, 16),
    declarant: emptyParty(), consignor: emptyParty(), consignee: emptyParty(),
    goods: [emptyItem(1)],
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);

  useEffect(() => {
    // Auto-fill LRN on mount
    setForm((f) => ({ ...f, lrn: `CPACT-${Date.now()}` }));
  }, []);

  const loadSample = async () => {
    setSampleLoading(true);
    try {
      const r = await ics2API.sample();
      const s = r.data;
      // Normalize estimated_arrival for datetime-local input
      s.estimated_arrival = (s.estimated_arrival || '').slice(0, 16);
      setForm(s);
      toast.success('Sample loaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load sample');
    } finally {
      setSampleLoading(false);
    }
  };

  const buildPayload = () => ({
    ...form,
    estimated_arrival: form.estimated_arrival
      ? new Date(form.estimated_arrival).toISOString()
      : new Date().toISOString(),
    goods: form.goods.map((g) => ({
      ...g,
      item_number: Number(g.item_number) || 1,
      gross_mass_kg: Number(g.gross_mass_kg) || 0,
      net_mass_kg: g.net_mass_kg === '' || g.net_mass_kg === null ? null : Number(g.net_mass_kg),
      quantity: g.quantity === '' || g.quantity === null ? null : Number(g.quantity),
      cbam_embedded_co2_t: g.cbam_embedded_co2_t === '' || g.cbam_embedded_co2_t === null
        ? null : Number(g.cbam_embedded_co2_t),
      country_of_origin: g.country_of_origin || null,
    })),
  });

  const doPreview = async () => {
    setLoading(true);
    try {
      const r = await ics2API.preview(buildPayload());
      setPreview(r.data);
      if (r.data.warnings?.length) {
        toast.warning(`${r.data.warnings.length} validation warning(s)`);
      } else {
        toast.success('Preview generated — no warnings');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail ? JSON.stringify(e.response.data.detail) : 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const doDownload = async () => {
    setLoading(true);
    try {
      const res = await ics2API.generate(buildPayload());
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ICS2_ENS_${form.lrn}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('XML downloaded');
    } catch (e) {
      toast.error('Download failed — check required fields');
    } finally {
      setLoading(false);
    }
  };

  const updatePartyField = (partyKey, field, value) => {
    setForm((f) => ({ ...f, [partyKey]: { ...f[partyKey], [field]: value } }));
  };

  const updateItem = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      goods: f.goods.map((g, i) => (i === idx ? { ...g, [field]: value } : g)),
    }));
  };

  const addItem = () => setForm((f) => ({ ...f, goods: [...f.goods, emptyItem(f.goods.length + 1)] }));
  const removeItem = (idx) => setForm((f) => ({
    ...f,
    goods: f.goods.filter((_, i) => i !== idx).map((g, i) => ({ ...g, item_number: i + 1 })),
  }));

  const partyCard = (partyKey, title) => (
    <Card className="p-4" data-testid={`party-${partyKey}`}>
      <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        <Field label="EORI" value={form[partyKey].eori} onChange={(v) => updatePartyField(partyKey, 'eori', v)} testId={`${partyKey}-eori`} />
        <Field label="Name" value={form[partyKey].name} onChange={(v) => updatePartyField(partyKey, 'name', v)} testId={`${partyKey}-name`} />
        <Field label="Street" value={form[partyKey].street} onChange={(v) => updatePartyField(partyKey, 'street', v)} testId={`${partyKey}-street`} />
        <Field label="City" value={form[partyKey].city} onChange={(v) => updatePartyField(partyKey, 'city', v)} testId={`${partyKey}-city`} />
        <Field label="Postcode" value={form[partyKey].postcode} onChange={(v) => updatePartyField(partyKey, 'postcode', v)} testId={`${partyKey}-postcode`} />
        <Field label="Country (ISO-2)" value={form[partyKey].country} onChange={(v) => updatePartyField(partyKey, 'country', v.toUpperCase())} testId={`${partyKey}-country`} maxLength={2} />
      </div>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="ics2-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCode2 className="w-6 h-6 text-primary" /> ICS2 ENS XML Generator
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Build an Entry Summary Declaration (EU ICS2) with embedded CBAM CO₂ fields.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadSample} disabled={sampleLoading} data-testid="load-sample-btn">
            {sampleLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Load sample
          </Button>
          <Button variant="outline" onClick={doPreview} disabled={loading} data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button onClick={doDownload} disabled={loading} data-testid="download-xml-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download XML
          </Button>
        </div>
      </div>

      {/* Header section */}
      <Card className="p-4" data-testid="declaration-header">
        <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Declaration Header</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="LRN (Local Ref. Number)" value={form.lrn} onChange={(v) => setForm({ ...form, lrn: v })} testId="field-lrn" />
          <div>
            <Label className="text-xs">Declaration type</Label>
            <Select value={form.declaration_type} onValueChange={(v) => setForm({ ...form, declaration_type: v })}>
              <SelectTrigger data-testid="field-declaration-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ENS">ENS — new declaration</SelectItem>
                <SelectItem value="ENS_AMEND">ENS_AMEND — amendment</SelectItem>
                <SelectItem value="ENS_INVAL">ENS_INVAL — invalidation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Transport mode</Label>
            <Select value={form.transport_mode} onValueChange={(v) => setForm({ ...form, transport_mode: v })}>
              <SelectTrigger data-testid="field-transport-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRANSPORT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Field label="Conveyance ID (IMO/flight/plate)" value={form.conveyance_id} onChange={(v) => setForm({ ...form, conveyance_id: v })} testId="field-conveyance" />
          <Field label="Loading port (UN/LOCODE)" value={form.loading_port} onChange={(v) => setForm({ ...form, loading_port: v.toUpperCase() })} testId="field-loading-port" maxLength={5} />
          <Field label="Arrival port (UN/LOCODE)" value={form.arrival_port} onChange={(v) => setForm({ ...form, arrival_port: v.toUpperCase() })} testId="field-arrival-port" maxLength={5} />
          <div className="md:col-span-3">
            <Label className="text-xs">Estimated arrival (UTC)</Label>
            <Input type="datetime-local" value={form.estimated_arrival} onChange={(e) => setForm({ ...form, estimated_arrival: e.target.value })} data-testid="field-eta" />
          </div>
        </div>
      </Card>

      {/* Parties */}
      <div className="grid md:grid-cols-3 gap-4">
        {partyCard('declarant', 'Declarant')}
        {partyCard('consignor', 'Consignor')}
        {partyCard('consignee', 'Consignee')}
      </div>

      {/* Goods */}
      <Card className="p-4" data-testid="goods-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
            Goods items ({form.goods.length})
          </h3>
          <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add item
          </Button>
        </div>
        <div className="space-y-3">
          {form.goods.map((g, idx) => (
            <div key={idx} className="grid md:grid-cols-12 gap-2 items-end border-b border-border pb-3" data-testid={`goods-row-${idx}`}>
              <div className="md:col-span-1">
                <Label className="text-xs">#</Label>
                <Input value={g.item_number} disabled />
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs">Description</Label>
                <Input value={g.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} data-testid={`item-${idx}-desc`} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">HS code</Label>
                <Input value={g.hs_code} onChange={(e) => updateItem(idx, 'hs_code', e.target.value)} data-testid={`item-${idx}-hs`} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Gross mass (kg)</Label>
                <Input type="number" value={g.gross_mass_kg} onChange={(e) => updateItem(idx, 'gross_mass_kg', e.target.value)} data-testid={`item-${idx}-gross`} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">CBAM CO₂ (t)</Label>
                <Input type="number" step="0.001" value={g.cbam_embedded_co2_t ?? ''} onChange={(e) => updateItem(idx, 'cbam_embedded_co2_t', e.target.value)} data-testid={`item-${idx}-cbam`} />
              </div>
              <div className="md:col-span-1">
                <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={form.goods.length === 1} data-testid={`remove-item-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Country of origin (ISO-2)</Label>
                <Input value={g.country_of_origin || ''} onChange={(e) => updateItem(idx, 'country_of_origin', e.target.value.toUpperCase())} maxLength={2} data-testid={`item-${idx}-origin`} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Net mass (kg)</Label>
                <Input type="number" value={g.net_mass_kg ?? ''} onChange={(e) => updateItem(idx, 'net_mass_kg', e.target.value)} data-testid={`item-${idx}-net`} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Quantity</Label>
                <Input type="number" value={g.quantity ?? ''} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} data-testid={`item-${idx}-qty`} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Unit</Label>
                <Input value={g.quantity_unit || ''} onChange={(e) => updateItem(idx, 'quantity_unit', e.target.value.toUpperCase())} data-testid={`item-${idx}-unit`} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Preview output */}
      {preview && (
        <Card className="p-4" data-testid="preview-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Preview — {preview.filename}</h3>
            <Badge variant="outline" className="font-mono">{preview.size_bytes} bytes</Badge>
          </div>
          {preview.warnings?.length > 0 && (
            <div className="mb-3 rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm" data-testid="preview-warnings">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <AlertTriangle className="w-4 h-4" /> {preview.warnings.length} validation warning(s)
              </div>
              <ul className="list-disc list-inside space-y-0.5 text-xs text-muted-foreground">
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <pre className="text-xs bg-muted/40 p-3 rounded border border-border overflow-auto max-h-[480px]" data-testid="preview-xml">
            {preview.xml}
          </pre>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, maxLength }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} maxLength={maxLength} />
    </div>
  );
}
